import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  InboxItem,
  InboxItemKind,
  InboxItemRef,
  InboxItemState,
  InboxListResponse,
  SpreadsheetPreview,
} from '@/types/inbox'
import type { InvoiceInput } from '@/types/database'
import type { InvoiceExtraction } from '@/types/invoiceAnalyser'

const API_URL = import.meta.env.VITE_API_URL || '/v1'

async function apiCall<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(body.error || body.message || 'Request failed')
  }

  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** URL of the original document, embeddable when inline is requested. */
export function documentUrl(documentId: string, inline = false): string {
  return `${API_URL}/documents/${documentId}/file${inline ? '?inline=1' : ''}`
}

/** Enclosures filed with the mail of an accepted invoice (Database preview). */
export async function fetchInvoiceEnclosures(invoiceId: string): Promise<InboxItemRef[]> {
  const data = await apiCall<{ enclosures: InboxItemRef[] }>(
    `/inbox/enclosures?invoiceId=${encodeURIComponent(invoiceId)}`,
  )
  return data?.enclosures ?? []
}

/** What the server answers for a hand-off to Flixpector. */
export interface FlixpectorHandOff {
  /** The inbox item as it now stands, in state sent_to_flixpector */
  item: InboxItem | null
  flixpectorId: string
  invoiceId: string | null
  /** False when the document arrived but its analysis did not start */
  analysisStarted: boolean
  handlerSet: boolean
}

/**
 * Push a document analysed in the Invoice Analyser straight to Flixpector.
 *
 * A plain function rather than part of useInbox(), because the analyser page has
 * no inbox list to refresh. It lives here all the same: what this creates is an
 * inbox item, so the invoice then sits under "Bei Flixpector" next to everything
 * that arrived by mail and comes back through the same sync.
 */
export async function sendAnalyserResultToFlixpector(payload: {
  documentId: string
  filename?: string | null
  extraction: InvoiceExtraction
  /** Required: the server refuses a hand-off without a named Bearbeiter */
  assigneeUserId: string
  poId?: string | null
}): Promise<FlixpectorHandOff> {
  return apiCall<FlixpectorHandOff>('/inbox/from-analyser', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

/** How many items sit in each lifecycle state — drives the tab counters. */
export type InboxCounts = Record<InboxItemState | 'all', number>

function countByState(items: InboxItem[]): InboxCounts {
  const counts: InboxCounts = {
    all: items.length,
    new: 0,
    sent_to_flixpector: 0,
    returned_from_flixpector: 0,
    accepted: 0,
    dismissed: 0,
  }
  for (const item of items) counts[item.state] += 1
  return counts
}

export function useInbox(initialState: InboxItemState = 'new') {
  const [allItems, setAllItems] = useState<InboxItem[]>([])
  const [pending, setPending] = useState(0)
  const [mailbox, setMailbox] = useState<string | null>(null)
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null)
  const [stateFilter, setStateFilter] = useState<InboxItemState>(initialState)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Always fetches the whole inbox, not just the selected tab. A triage
   * mailbox is small, and holding all of it lets the tab counters, the search
   * box and the sort order work without a round trip per keystroke.
   */
  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiCall<InboxListResponse>('/inbox?state=all')
      setAllItems(data.items ?? [])
      setPending(data.pending ?? 0)
      setMailbox(data.mailbox ?? null)
      setLastSyncedAt(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Inbox konnte nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // The whole inbox is held in allItems for the counters; the list shows one
  // state at a time, since every tab is a distinct step of the queue.
  const items = useMemo(
    () => allItems.filter((i) => i.state === stateFilter),
    [allItems, stateFilter],
  )

  const counts = useMemo(() => countByState(allItems), [allItems])

  /** Take the invoice into the database — the "invoice is fine" path. */
  const accept = useCallback(
    async (id: string, overrides?: Partial<InvoiceInput>) => {
      await apiCall(`/inbox/${id}/accept`, {
        method: 'POST',
        body: JSON.stringify(overrides ?? {}),
      })
      await refresh()
    },
    [refresh],
  )

  /**
   * Hand it to Flixpector for review, naming the Bearbeiter who owns it from
   * here on. The name and initials travel with the upload, and the server
   * refuses a hand-off without one.
   */
  const sendToFlixpector = useCallback(
    async (id: string, assigneeUserId: string) => {
      await apiCall(`/inbox/${id}/flixpector`, {
        method: 'POST',
        body: JSON.stringify({ assigneeUserId }),
      })
      await refresh()
    },
    [refresh],
  )

  const remove = useCallback(
    async (id: string) => {
      await apiCall(`/inbox/${id}`, { method: 'DELETE' })
      await refresh()
    },
    [refresh],
  )

  /** Acknowledge without deleting — for a "no invoices this month" notice. */
  const dismiss = useCallback(
    async (id: string) => {
      await apiCall(`/inbox/${id}/dismiss`, { method: 'POST' })
      await refresh()
    },
    [refresh],
  )

  /** "Confirm as enclosure" is the plain dismiss on an item of kind enclosure. */
  const confirmEnclosure = dismiss

  const changeKind = useCallback(
    async (id: string, kind: InboxItemKind) => {
      await apiCall(`/inbox/${id}/kind`, { method: 'PATCH', body: JSON.stringify({ kind }) })
      await refresh()
    },
    [refresh],
  )

  /**
   * Pull the current review state from Flixpector. reopen=true also puts the
   * item back in front of the user, which is what the send button in Flixpector
   * triggers on its side.
   */
  const syncFlixpector = useCallback(
    async (id: string, reopen = false) => {
      await apiCall(`/inbox/${id}/sync-flixpector${reopen ? '?reopen=1' : ''}`, { method: 'POST' })
      await refresh()
    },
    [refresh],
  )

  const loadPreview = useCallback(
    (id: string) => apiCall<SpreadsheetPreview>(`/inbox/${id}/preview`),
    [],
  )

  return {
    items,
    counts,
    pending,
    mailbox,
    lastSyncedAt,
    stateFilter,
    setStateFilter,
    loading,
    error,
    refresh,
    accept,
    sendToFlixpector,
    remove,
    dismiss,
    confirmEnclosure,
    changeKind,
    syncFlixpector,
    loadPreview,
  }
}

/**
 * Just the pending count, for the nav badge. Polls slowly — mail is fetched
 * every 30 minutes, so anything faster is wasted traffic.
 */
export function useInboxCount(pollMs = 120_000) {
  const [pending, setPending] = useState(0)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const data = await apiCall<{ pending: number }>('/inbox/count')
        if (!cancelled) setPending(data.pending ?? 0)
      } catch {
        // A failing badge must never break the header
      }
    }

    void load()
    const timer = setInterval(() => void load(), pollMs)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [pollMs])

  return pending
}
